import { UserCustomFilter } from 'core/utils/types/user-custom-filter.type';
import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { filterCache } from './create-filter-cache.store';
import { FilterUrlSyncConfig, useFilterUrlSync } from './use-filter-url-sync.hook';

export interface FilterContextType<T> {
    originalFilter: UserCustomFilter<T>;
    currentFilter: UserCustomFilter<T>;
    isFilterReady: boolean;
    isFilterModified: boolean;
    isFilterSavedAndModified: boolean;
    isFilterEmpty: boolean;
    setCurrentFilter: Dispatch<SetStateAction<UserCustomFilter<T>>>;
    setNewOriginal: (filter: UserCustomFilter<T>) => void;
    resetToOriginal: VoidFunction;
}

export interface FilterContextConfig<T> {
    areFiltersEqual: (a: UserCustomFilter<T>, b: UserCustomFilter<T>) => boolean;
    isEmptyFilter: (filter: UserCustomFilter<T>) => boolean;
    defaultFilters?: { active: UserCustomFilter<T>; inactive: UserCustomFilter<T> };
    moduleKey?: string;
}

export interface FilterProviderProps<T> extends PropsWithChildren {
    defaultFilter?: UserCustomFilter<T>;
}

export function createFilterContext<T>(config: FilterContextConfig<T>) {
    const FilterContext = createContext<FilterContextType<T> | null>(null);

    const FilterUrlSyncBridge = ({
        defaultFilters,
        setNewOriginal,
        restoreFromCache,
        moduleKey,
        cacheEnabledRef,
    }: FilterUrlSyncConfig<T> & { moduleKey?: string }) => {
        useFilterUrlSync<T>({ defaultFilters, setNewOriginal, restoreFromCache, moduleKey, cacheEnabledRef });
        return null;
    };

    const FilterProvider = ({ children, defaultFilter }: FilterProviderProps<T>) => {
        const initialFilter = defaultFilter ?? config.defaultFilters?.active;
        if (!initialFilter) {
            throw new Error(
                'FilterProvider requires either a "defaultFilter" prop or "defaultFilters" in the context config.'
            );
        }

        // Register this module as active (clears other module's cache)
        useEffect(() => {
            if (config.moduleKey) {
                filterCache.setActiveModule(config.moduleKey);
            }
        }, []);

        // Controls whether filter changes are synced to the cache store.
        // Disabled when on an explicit URL filter (saved view); enabled on base URL.
        const cacheEnabledRef = useRef(true);

        const [originalFilter, setOriginalFilter] = useState<UserCustomFilter<T>>(initialFilter);
        const [currentFilter, setCurrentFilter] = useState<UserCustomFilter<T>>(initialFilter);
        // isFilterReady starts as false by design: it prevents premature data fetches before
        // the URL/filter resolution completes. When config.defaultFilters is set, the internal
        // FilterUrlSyncBridge handles this automatically; otherwise an explicit setNewOriginal()
        // call is required.
        const [isFilterReady, setIsFilterReady] = useState(false);

        const isFilterModified = useMemo(
            () => !config.areFiltersEqual(currentFilter, originalFilter),
            [currentFilter, originalFilter]
        );

        const isFilterSavedAndModified = useMemo(
            () => currentFilter.id != null && isFilterModified,
            [currentFilter, isFilterModified]
        );

        const isFilterEmpty = useMemo(() => config.isEmptyFilter(currentFilter), [currentFilter]);

        const resetToOriginal = useCallback(() => {
            setCurrentFilter(originalFilter);
        }, [originalFilter]);

        const setNewOriginal = useCallback((filter: UserCustomFilter<T>) => {
            setOriginalFilter(filter);
            setCurrentFilter(filter);
            setIsFilterReady(true);
        }, []);

        // Rehydrates both filters independently from the cache so the user's "modified"
        // relationship survives re-runs of the URL sync effect (e.g., when sideMenuEntries
        // resolves async after the user has already started editing).
        const restoreFromCache = useCallback(
            (cachedOriginal: UserCustomFilter<T>, cachedCurrent: UserCustomFilter<T>) => {
                setOriginalFilter(cachedOriginal);
                setCurrentFilter(cachedCurrent);
                setIsFilterReady(true);
            },
            []
        );

        // Sync filter changes to the cache store (only when caching is enabled)
        useEffect(() => {
            if (config.moduleKey && isFilterReady && cacheEnabledRef.current) {
                filterCache.cacheFilter(config.moduleKey, {
                    originalFilter: originalFilter as UserCustomFilter<unknown>,
                    currentFilter: currentFilter as UserCustomFilter<unknown>,
                });
            }
        }, [originalFilter, currentFilter, isFilterReady]);

        const value = useMemo<FilterContextType<T>>(
            () => ({
                originalFilter,
                currentFilter,
                isFilterReady,
                isFilterModified,
                isFilterSavedAndModified,
                isFilterEmpty,
                setCurrentFilter,
                setNewOriginal,
                resetToOriginal,
            }),
            [
                originalFilter,
                currentFilter,
                isFilterReady,
                isFilterModified,
                isFilterSavedAndModified,
                isFilterEmpty,
                setNewOriginal,
                resetToOriginal,
            ]
        );

        return (
            <FilterContext.Provider value={value}>
                {config.defaultFilters && (
                    <FilterUrlSyncBridge
                        defaultFilters={config.defaultFilters}
                        setNewOriginal={setNewOriginal}
                        restoreFromCache={restoreFromCache}
                        moduleKey={config.moduleKey}
                        cacheEnabledRef={cacheEnabledRef}
                    />
                )}
                {children}
            </FilterContext.Provider>
        );
    };

    const useFilterContext = (): FilterContextType<T> => {
        const context = useContext(FilterContext);
        if (!context) {
            throw new Error('useFilterContext must be used within its FilterProvider');
        }
        return context;
    };

    return { FilterProvider, useFilterContext };
}
